import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { QUERY_KEYS } from 'constants/constants'
import { Career } from 'types/career.types'
import { usePartnerContext, useUserContext } from '../context'
import { mutation, query } from '../graphql'
import { notifyBugsnag } from '../helpers'

export interface CareerWithSpaces extends Career {
  number_of_spaces: number | 'n/a'
}

const checkHasChallengeConfigId = ({
  id: career_id,
  status,
  challenge_config_id
}) => {
  if (
    status === 'ACTIVE' &&
    !challenge_config_id &&
    !career_id.includes('PRODUCT_TOUR')
  ) {
    notifyBugsnag(
      new Error(`challenge_config_id is missing - career_id: ${career_id}`)
    )
  }
}

const deleteIfIsProductTourCareer = ({ career_id, user_id }) => {
  if (career_id === `PRODUCT_TOUR-${user_id}`) {
    mutation({
      mutation: 'deleteCareer',
      input: { id: career_id },
      authMode: 'AMAZON_COGNITO_USER_POOLS'
    })
  }
}

export const useCareersQuery = ({ select, initialData }) => {
  const { partner } = usePartnerContext()
  const {
    activeSpace,
    cognitoUser: { username }
  } = useUserContext()

  const queryClient = useQueryClient()

  const spaces_to_careers = useMemo(() => {
    const { state } =
      queryClient
        .getQueryCache()
        .find({
          queryKey: QUERY_KEYS.talentsByPartner(partner.id, username)
        }) || {}

    if (state?.status !== 'success') return null

    const result = new Map()

    // queryKey: ['talents', partner.id, username]
    // queryFn: () => Promise.all([getAllSpaces(), getCareers()])
    // select: [0] = spaces / [1] = careers
    state.data[0].forEach((space) =>
      result.set(
        space.career_id,
        (result.get(space.career_id) || []).concat([{ ...space }])
      )
    )

    return result
  }, [queryClient, partner.id, username])

  const getNumberOfSpaces = useCallback(
    ({ id, status }) => {
      if (!spaces_to_careers || status !== 'ACTIVE') return 'n/a'
      if (!spaces_to_careers.get(id)) return 0

      return spaces_to_careers.get(id).length
    },
    [spaces_to_careers]
  )

  return useQuery<CareerWithSpaces[]>({
    queryKey: QUERY_KEYS.careersByPartner(partner.id),
    queryFn: async () => {
      const response = await query({
        query: 'careerByPartnerByDate',
        variables: { partner_id: partner.id },
        authMode: 'AMAZON_COGNITO_USER_POOLS'
      })

      response.forEach((item: Career) => {
        checkHasChallengeConfigId(item)
        deleteIfIsProductTourCareer({
          career_id: item.id,
          user_id: activeSpace.id
        })
      })

      return response.map((item: Career) => ({
        ...item,
        number_of_spaces: getNumberOfSpaces(item)
      }))
    },
    select,
    initialData
  })
}
